import { ClientLoggingService } from '../../../../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ComplianceSection } from '../compliance.component';
import { MatCardModule } from '@angular/material/card';
import { ArchitecturalPlanPage } from '../../../../../shared/models/architectural-plan-page.model';
import { TabService } from '../../../../../shared/tab.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ComplianceRiskLabelPipe } from '../../../../../shared/pipes/compliance-risk-label.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { EvidenceCitationChipComponent } from '../../../../../shared/citations/evidence-citation-chip.component';
import {
  CitationDestination,
  EvidenceCitationLinkService,
} from '../../../../../shared/citations/evidence-citation-link.service';
import { mapEvidenceChain } from '../../../../../shared/citations/evidence-citation.mapper';
import {
  CitationRejection,
  CitationSet,
  EMPTY_CITATION_SET,
  EvidenceCitation,
} from '../../../../../shared/citations/evidence-citation.model';
import { renderReportBody } from '../../../../../shared/citations/citation-markdown';
import { CitationBodyDirective } from '../../../../../shared/citations/citation-body.directive';
import { ReportCodeIdentity } from '../../../../../shared/citations/inline-code-reference';

/**
 * First value that is a non-empty string, or `''` when none is.
 *
 * Exists because protobuf's `toObject()` represents an unset string as `''`, which `??` treats as
 * present. Anywhere a fallback is meant to fire on "the producer recorded nothing", emptiness is the
 * condition to test rather than nullishness.
 */
function firstPresent(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    const value = (candidate ?? '').trim();
    if (value) return value;
  }
  return '';
}

/** First value that is a positive number, or `0` when none is. The numeric counterpart of the above. */
function firstPositive(...candidates: Array<number | null | undefined>): number {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && candidate > 0) return candidate;
  }
  return 0;
}

/**
 * Tab view that displays a generated compliance report, with structured evidence citations
 * (Issue #1773).
 *
 * **The trusted-HTML boundary changed here, and it is the most important thing in this file.**
 * This component used to call `marked.parse()` on the model-authored compliance statement and hand
 * the result straight to `bypassSecurityTrustHtml()` with no sanitizer in between — unlike chat,
 * which has always run DOMPurify first. It now renders through
 * {@link renderReportBody}, which sanitizes against a closed tag allow-list before anything is
 * trusted, and which strips anchors entirely. A URL written in model prose is therefore visible text
 * and never a link.
 *
 * Every live link in the report body now comes from a structured citation that
 * {@link mapEvidenceChain} accepted: correct project, well-formed geometry, a supported selector
 * version, a validated code identity, and a project matching this report's own. The bypass is
 * narrower than it was, not wider.
 *
 * The header and hierarchy links also move onto the shared deterministic builder. The old
 * `getIccUrl()` and `getHierarchyIccUrl()` duplicated slug logic between themselves and returned
 * `'#'` when the code book was missing — a dead link that looks live until a reader clicks it.
 */
@Component({
  selector: 'app-compliance-report',
  imports: [
    CommonModule,
    MatCardModule,
    DatePipe,
    ComplianceRiskLabelPipe,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    EvidenceCitationChipComponent,
    CitationBodyDirective,
  ],
  templateUrl: './compliance-report-tab.component.html',
  styleUrls: ['./compliance-report-tab.component.scss'],
})
export class ComplianceReportComponent implements OnInit, OnDestroy {
  private tabService = inject(TabService);
  private linkService = inject(EvidenceCitationLinkService);
  private router = inject(Router);

  section!: ComplianceSection;
  activePlanPage: ArchitecturalPlanPage | null = null;

  /**
   * Sanitized report body carrying a placeholder per resolved citation.
   *
   * One string rather than a list of fragments. {@link CitationBodyDirective} writes it and attaches
   * a citation component to each placeholder, which keeps the markup balanced and keeps every
   * trusted string a whole sanitized document.
   */
  bodyHtml = '';

  /** Accepted citations for this report, in evidence-chain order, for the sources list. */
  citations: EvidenceCitation[] = [];

  /** Citations the resolver refused, so the reader can be told rather than left guessing. */
  rejections: CitationRejection[] = [];

  /** Citation IDs the prose referenced that this report does not supply. */
  unknownCitationIds: string[] = [];

  chapterInfo: { number: string; title: string } | null = null;
  private destroy$ = new Subject<void>();

  /** Maps a citation's identity to its one-based marker number, so markers and sources agree. */
  markerIndexById = new Map<string, number>();

  /**
   * Catalog identity of the report, recomputed only when the report itself changes.
   *
   * A field rather than a method called from the template, and that distinction is load-bearing
   * rather than stylistic. `[reportCode]="reportCode()"` returns a new object literal on every
   * change-detection cycle; Angular compares object inputs by reference, so the directive's
   * `ngOnChanges` fired every cycle and it destroyed and rebuilt every citation component each time.
   * Measured against the deployed build, the inline link element was replaced in eight of ten
   * samples over three seconds.
   *
   * The visible symptom is not a slow page. It is that the anchor can be destroyed between a
   * reader's mousedown and mouseup, so following a link does nothing — the feature works in the DOM
   * and is broken in the hand. Every unit test passed because each renders once; none ran a second
   * change-detection cycle.
   */
  reportCode: ReportCodeIdentity | null = null;

  /** Sources worth listing, recomputed with the report rather than on every cycle. See above. */
  visibleSources: EvidenceCitation[] = [];

  /** Resolved citations by ID, for the directive to look up from a placeholder. */
  citationsById: ReadonlyMap<string, EvidenceCitation> = new Map();

  /**
   * Section titles for inline code-link tooltips, from the contents this page already loaded.
   *
   * A field updated by subscription rather than a template call, for the same reason as
   * {@link reportCode}: the directive compares inputs by value, and a fresh map on every
   * change-detection cycle would rebuild every link.
   */
  sectionTitles: ReadonlyMap<string, string> = new Map();

  async ngOnInit(): Promise<void> {
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      this.activePlanPage = page;
      // Which sources are redundant depends on the page being viewed, so a page change has to
      // recompute them. Recomputing here rather than in the template is the same discipline as
      // reportCode: a new array on every cycle would rebuild the list each time.
      this.visibleSources = this.computeVisibleSources();
    });

    this.tabService.codeSectionTitles$.pipe(takeUntil(this.destroy$)).subscribe(titles => {
      this.sectionTitles = titles;
    });

    this.tabService.activeTabData$.pipe(takeUntil(this.destroy$)).subscribe(async data => {
      if (!data) return;
      this.section = data;
      ClientLoggingService.log('INFO', 'ComplianceReport: section data received:', this.section);
      await this.renderReport();
      this.extractChapterInfo();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Resolves this report's evidence and renders its body.
   *
   * Ordering matters: citations are resolved first so the renderer can decide, for each marker,
   * whether it names something this report actually supplies. A marker naming an unknown ID becomes
   * a visible unresolved state rather than silently disappearing.
   */
  private async renderReport(): Promise<void> {
    const citationSet = this.resolveCitations();
    this.citations = citationSet.citations;
    this.rejections = citationSet.rejections;
    this.markerIndexById = new Map(citationSet.citations.map((citation, index) => [citation.id, index + 1]));

    if (citationSet.rejections.length) {
      // Logged rather than shown verbatim: a reader needs to know a source could not be restored,
      // not to read a validator's internals.
      ClientLoggingService.log('WARN', '[ComplianceReport] refused citations:', citationSet.rejections);
    }

    this.citationsById = citationSet.byId;
    // Recomputed here, once per report, so the directive's inputs keep stable references between
    // change-detection cycles.
    this.reportCode = this.computeReportCode();
    this.visibleSources = this.computeVisibleSources();

    if (!this.section?.complianceStatement) {
      this.bodyHtml = '';
      this.unknownCitationIds = [];
      return;
    }

    const rendered = await renderReportBody(this.section.complianceStatement, citationSet);
    this.unknownCitationIds = rendered.unknownCitationIds;
    this.bodyHtml = rendered.html;
  }

  /**
   * Validates this report's evidence chain against the report's own identity.
   *
   * The anchor is the report's own recorded project, file, and page whenever it has them. A report
   * generated after #1773 always does, because the RPC response carries its originating request.
   *
   * **There is a fallback to the active page, and it applies to the project too.** An earlier version
   * of this comment claimed the project was never inferred from the active page, which was untrue of
   * the code beside it — a documented guarantee stronger than the implementation, caught in review of
   * PR #1824. What the code actually does, and why it is defensible:
   *
   * A cached report written before the request identity was carried through has no project of its
   * own, so the choice is between anchoring on the page the reader is currently viewing and refusing
   * to render any citation at all. The active page is a sound anchor because route guards already
   * enforce that the reader may view that project: anchoring there means "a citation may only point
   * at the project you are already authorized to be looking at". It is a different guarantee from
   * "the report says which project it belongs to", and weaker in the sense that it cannot detect a
   * report stored against the wrong project — but it is not a cross-tenant hole, because a citation
   * naming any *other* project is still refused with `unauthorizedProject`.
   *
   * What has not changed: the anchor is never taken from the citation itself. A citation states a
   * project only to be checked against this value, never to supply it.
   */
  private resolveCitations(): CitationSet {
    // `??` is deliberately not used here, and that was a real bug. These fields come from a protobuf
    // response via toObject(), where an unset string is '' and an unset number is 0 rather than
    // undefined -- so `section.architecturalPlanId ?? activePlanPage…` never reached the fallback it
    // was written to express. A cached report that recorded no plan ID produced planId '' and then an
    // empty citation set, meaning it rendered NO citations even though the backend had attached them.
    // That silently defeated this issue's "cached legacy reports continue to render" criterion.
    // Raised by security review of PR #1824 as finding F3.
    const planId = firstPresent(this.section?.architecturalPlanId, this.activePlanPage?.getArchitecturalPlanId());
    if (!planId) return EMPTY_CITATION_SET;

    return mapEvidenceChain(this.section?.evidenceChain ?? [], {
      planId,
      fileId: firstPresent(this.section?.evidenceFileId, this.activeFileId()),
      pageNumber: firstPositive(this.section?.evidencePageNumber, this.activePlanPage?.getPageNumber()),
    });
  }

  /**
   * File ID of the page currently being read, derived from its folder path.
   *
   * Mirrors the extraction `ComplianceComponent` already does for report generation (#167), and
   * falls back to file `1` for legacy single-file projects, which is the same fallback chat page
   * links and `FilePageLinkService` use.
   */
  activeFileId(): string {
    const pageFolderPath = this.activePlanPage?.getPageFolderPath() ?? '';
    return pageFolderPath.match(/\/files\/([^/]+)\/pages\/\d+\/?$/)?.[1] ?? '1';
  }

  /** Page number currently being read, or 0 when no page is active. */
  activePageNumber(): number {
    return this.activePlanPage?.getPageNumber() ?? 0;
  }

  /** Whether this report was opened from a validated file-summary drill-down. */
  get showFileSummaryReturn(): boolean {
    return this.fileSummaryDestination() !== null;
  }

  /**
   * Returns to the canonical file Compliance route. Query values are treated only as bounded filter
   * identifiers; project, file, and section identity come from the active authorized report.
   */
  returnToFileSummary(): void {
    const destination = this.fileSummaryDestination();
    if (!destination) return;
    const preservedState = (globalThis.history?.state as { fileComplianceReturn?: unknown } | undefined)
      ?.fileComplianceReturn;
    void this.router.navigate(['/projects', destination.projectId, 'files', destination.fileId, 'compliance'], {
      queryParams: {
        book: destination.book,
        section: destination.section,
        risk: destination.risk,
      },
      state: preservedState ? { fileComplianceReturn: preservedState } : undefined,
    });
  }

  private fileSummaryDestination(): {
    projectId: string;
    fileId: string;
    book: string;
    section: string;
    risk: 'high' | 'medium' | 'low' | 'missing' | 'stale';
  } | null {
    if (!this.activePlanPage || !this.section) return null;
    let query: Record<string, string>;
    try {
      query = this.router.parseUrl(this.router.url).queryParams;
    } catch {
      return null;
    }
    const risk = query['risk'];
    const book = query['book']?.trim();
    const projectId = this.activePlanPage.getArchitecturalPlanId().trim();
    const fileId = this.strictActiveFileId();
    const section = this.section.sectionId?.trim();
    if (
      query['from'] !== 'file-compliance' ||
      !book ||
      book.length > 200 ||
      !/^[a-zA-Z0-9._:-]+$/.test(book) ||
      !projectId ||
      !fileId ||
      !section ||
      !this.isFileComplianceRisk(risk)
    ) {
      return null;
    }
    return { projectId, fileId, book, section, risk };
  }

  /** Returns a route-provided file ID without applying the legacy single-file fallback. */
  private strictActiveFileId(): string | null {
    const pageFolderPath = this.activePlanPage?.getPageFolderPath() ?? '';
    return pageFolderPath.match(/\/files\/([^/]+)\/pages\/\d+\/?$/)?.[1]?.trim() || null;
  }

  private isFileComplianceRisk(value: string | undefined): value is 'high' | 'medium' | 'low' | 'missing' | 'stale' {
    return value === 'high' || value === 'medium' || value === 'low' || value === 'missing' || value === 'stale';
  }

  /**
   * Catalog identity of the report, for the inline code-section links in its prose (Issue #1773).
   *
   * Assembled from the structured payload only — the code book's document slug, and the chapter entry
   * of the section hierarchy. Nothing here comes from the statement text, which is the property that
   * lets an inline link be trusted: prose supplies a section number and this supplies everything that
   * reaches the URL.
   *
   * Returns `null` when the payload cannot identify the provision, in which case references stay as
   * plain text rather than becoming links built on guessed identity.
   */
  private computeReportCode(): ReportCodeIdentity | null {
    const hierarchy = this.section?.hierarchy;
    const documentSlug = this.section?.codeBook?.document_slug;
    if (!hierarchy?.length || !documentSlug) return null;

    // The section under review is the deepest hierarchy entry, the same one the report header names.
    const sectionId = hierarchy[hierarchy.length - 1]?.id ?? this.section?.sectionId ?? '';
    if (!sectionId) return null;

    const chapterItem = hierarchy.length >= 2 ? hierarchy[1] : undefined;
    return {
      bookId: String(this.section?.codeBook?.id ?? ''),
      documentSlug,
      chapterSlug: chapterItem?.chapterSlug ?? '',
      chapterTitle: chapterItem?.title ?? '',
      sectionId,
    };
  }

  /**
   * The citations worth listing under the report, which is not all of them (Issue #1773).
   *
   * The deterministic pair this report can always produce — the page it analyzed and the provision it
   * was asked about — turned out to tell a reader nothing they could not already see. The page
   * citation points at the page they are looking at, and the code citation repeats the link already in
   * the report header. Two entries under a heading called "Sources" implied there was somewhere to go.
   *
   * So an entry is listed only when it adds something: a page other than the one on screen, or a
   * provision other than the one under review. On today's reports both rules fire and the section
   * disappears entirely, which is the intended outcome. Nothing is deleted, so when #1843 adds region
   * and text citations — neither of which duplicates anything visible — the list returns on its own.
   */
  private computeVisibleSources(): EvidenceCitation[] {
    const reportSectionId = this.reportCode?.sectionId ?? '';
    return this.citations.filter(citation => !this.isRedundantSource(citation, reportSectionId));
  }

  /** Whether one citation only repeats something the reader can already see on this screen. */
  private isRedundantSource(citation: EvidenceCitation, reportSectionId: string): boolean {
    if (citation.kind === 'page') {
      const samePage = citation.page.pageNumber === this.activePageNumber();
      const sameFile = (citation.page.fileId || '1') === (this.activeFileId() || '1');
      return samePage && sameFile;
    }
    if (citation.kind === 'code') {
      return !!reportSectionId && citation.code.sectionId === reportSectionId;
    }
    // Region and text evidence names something no other part of the page shows, so it always earns
    // its entry.
    return false;
  }

  /** One-based marker number for a citation, used by the sources list to match the inline markers. */
  markerFor(citation: EvidenceCitation): number {
    return this.markerIndexById.get(citation.id) ?? 1;
  }

  /**
   * Stable absolute URL for a citation, printed in the sources list so a paper copy stays useful.
   *
   * Application routes only. A temporary signed URL would be useless on paper the moment it expired,
   * which is why the issue forbids embedding one in exported output.
   */
  printableUrl(citation: EvidenceCitation): string {
    const destination: CitationDestination = this.linkService.buildDestination(citation);
    if (destination.kind === 'internal') return destination.url;
    if (destination.kind === 'external') return destination.href;
    return '';
  }

  extractChapterInfo(): void {
    if (this.section && this.section.hierarchy && this.section.hierarchy.length > 1) {
      // The second item in the hierarchy should be the chapter (first is book)
      const chapterItem = this.section.hierarchy[1];

      if (chapterItem && chapterItem.title) {
        // Extract chapter number from title (e.g., "Chapter 11: Accessibility" -> "11")
        const chapterMatch = chapterItem.title.match(/Chapter\s+(\d+)/i);
        const chapterNumber = chapterMatch ? chapterMatch[1] : '';

        this.chapterInfo = {
          number: chapterNumber,
          title: chapterItem.title,
        };
      }
    }
  }

  getComplianceClass(status: string | undefined): string {
    if (!status) return '';
    return 'compliance-' + status.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Canonical code URL for one hierarchy entry, or `''` when one cannot be built.
   *
   * Replaces the previous `getIccUrl()` and `getHierarchyIccUrl()` pair, which duplicated slug logic
   * between themselves and returned `'#'` on missing data. Returning the empty string lets the
   * template render plain text instead of an anchor that goes nowhere.
   *
   * The hierarchy is `[book, chapter, …section levels]`, so the entry's depth decides how specific
   * the URL may be: the book alone, the book and chapter, or the chapter plus a section fragment.
   */
  hierarchyUrl(hierarchyItemId: string | undefined): string {
    const hierarchy = this.section?.hierarchy;
    const documentSlug = this.section?.codeBook?.document_slug;
    if (!hierarchy?.length || !documentSlug) return '';

    // An entry with no ID cannot be linked, and must not fall through to the comparisons below.
    // `ComplianceSection.hierarchy[].id` is optional, and with an undefined ID both `isBook` and
    // `isChapter` would compare undefined against undefined and come out true — the first whenever
    // the book entry has no ID of its own, the second whenever there is no chapter entry at all,
    // because optional chaining on a missing `chapterItem` also yields undefined. The function would
    // then return the *book* URL for an entry that is not the book, and the template would render it
    // as a link labelled with that entry's own title. A reader clicking what reads as a section would
    // land on the document root. Caught in review of PR #1824.
    const itemId = hierarchyItemId?.trim();
    if (!itemId) return '';

    const chapterItem = hierarchy.length >= 2 ? hierarchy[1] : undefined;
    // Both sides must be present for an identity comparison to mean anything.
    const isBook = !!hierarchy[0]?.id && hierarchy[0].id === itemId;
    const isChapter = !!chapterItem?.id && chapterItem.id === itemId;

    const destination = this.linkService.buildDestination({
      kind: 'code',
      id: `hierarchy-${itemId}`,
      claimId: '',
      referenceable: false,
      confidence: 'unknown',
      excerpt: '',
      provenance: {
        method: 'deterministic',
        generatedByModel: '',
        pageArtifactRevision: '',
        regionHierarchyRevision: '',
      },
      code: {
        bookId: String(this.section?.codeBook?.id ?? ''),
        documentSlug,
        // Asking for the book means asking for no chapter, which is how the builder is told to stop
        // at the document root instead of appending a chapter path.
        chapterSlug: isBook ? '' : (chapterItem?.chapterSlug ?? ''),
        chapterTitle: isBook ? '' : (chapterItem?.title ?? ''),
        // Only a section-level entry gets a fragment. A fragment on a chapter entry would point at
        // an anchor that is not on that page.
        sectionId: isBook || isChapter ? '' : itemId,
        visibleLabel: itemId,
      },
    });

    return destination.kind === 'external' ? destination.href : '';
  }

  /** Canonical code URL for the section this report is about. */
  sectionUrl(): string {
    const hierarchy = this.section?.hierarchy;
    if (!hierarchy?.length) return '';
    // The section under report is the deepest hierarchy entry, which is also the one the report
    // header names. Reusing hierarchyUrl keeps one slug implementation rather than two.
    return this.hierarchyUrl(hierarchy[hierarchy.length - 1]?.id ?? this.section?.sectionId);
  }
}
